import React, { useMemo, useRef } from "react";
import { Table } from "antd";
import type { TablePaginationConfig, TableProps } from "antd";
import type { Reference as TableRef } from "@rc-component/table";
import type {
  ColumnsType,
  FilterValue,
  Key,
  RowSelectMethod,
  SorterResult,
  TableCurrentDataSource,
  TableRowSelection,
} from "antd/es/table/interface";
import { useTableHeight } from "@/utils/tableHelpers";

// Space below the table body that must stay inside the viewport: the column
// header row (~40px), the pagination bar (~64px), and page bottom padding.
const DEFAULT_RESERVED_HEIGHT = 120;

type PaginatedTableProps<T extends object> = {
  data?: T[];
  columns: ColumnsType<T>;
  loading?: boolean;
  setPagination: (pagination: TablePaginationConfig) => void;
  /** Space (px) kept below the table body for header, pagination and page padding. */
  reservedHeight?: number;
  minHeight?: number;
  pagination: TablePaginationConfig;
  footer?: TableProps<T>["footer"];
  tableRef?: React.Ref<TableRef>;
  rowKey?: TableProps<T>["rowKey"];
  className?: string;
  bordered?: boolean;
  onTableChange?: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<T> | SorterResult<T>[],
    extra: TableCurrentDataSource<T>,
  ) => void;
  onRow?: TableProps<T>["onRow"];
  onRowContextMenu?: (record: T, event: React.MouseEvent<HTMLElement>) => void;
  /**
   * Optional multi-select. `selectedRowKeys` and the keys handed to `onChange`
   * are the caller's own row keys (whatever `rowKey` resolves), not the
   * position-suffixed key this table uses internally to keep duplicate
   * business identifiers distinct.
   */
  rowSelection?: TableRowSelection<T>;
};

type TableRowWithInternalKey<T> = T & {
  __paginatedTableRowKey: Key;
};

export default function PaginatedTable<T extends object>({
  data,
  columns,
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address no-unused-vars */
  loading,
  setPagination,
  reservedHeight = DEFAULT_RESERVED_HEIGHT,
  minHeight,
  pagination,
  footer,
  tableRef,
  rowKey,
  className,
  bordered = false,
  onTableChange,
  onRow,
  onRowContextMenu,
  rowSelection,
}: PaginatedTableProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { tableHeight } = useTableHeight({
    tableRef: containerRef,
    reservedHeight,
    minHeight,
  });

  const { resolvedData, callerKeyByInternalKey } = useMemo(() => {
    const rows = data || [];
    const keyMap = new Map<Key, Key>();

    const mapped = rows.map((record, index) => {
      let baseKey: Key | undefined;

      if (typeof rowKey === "function") {
        baseKey = rowKey(record);
      } else if (typeof rowKey === "string") {
        baseKey = (record as Record<string, Key | undefined>)[rowKey];
      } else {
        baseKey = (record as Record<string, Key | undefined>).uniqueID ?? index;
      }

      const normalizedBaseKey = String(baseKey ?? "row");
      // Use the original fetched position to keep keys stable for this
      // result set even when duplicate business identifiers are reordered.
      const internalKey = `${normalizedBaseKey}__${index}`;
      keyMap.set(internalKey, baseKey ?? index);

      return { ...record, __paginatedTableRowKey: internalKey };
    }) as TableRowWithInternalKey<T>[];

    return { resolvedData: mapped, callerKeyByInternalKey: keyMap };
  }, [data, rowKey]);

  const internalRowSelection = useMemo(() => {
    if (!rowSelection) return undefined;

    const selectedCallerKeys = new Set(rowSelection.selectedRowKeys ?? []);
    return {
      ...rowSelection,
      selectedRowKeys: rowSelection.selectedRowKeys
        ? [...callerKeyByInternalKey.entries()]
            .filter(([, callerKey]) => selectedCallerKeys.has(callerKey))
            .map(([internalKey]) => internalKey)
        : undefined,
      onChange: rowSelection.onChange
        ? (
            internalKeys: Key[],
            selectedRows: T[],
            info: { type: RowSelectMethod },
          ) => {
            // The checkboxes only speak for the rows on this page, so keys
            // selected on other pages are carried through untouched instead of
            // being dropped by a page-scoped change.
            const pageKeys = new Set(callerKeyByInternalKey.values());
            const offPageKeys = (rowSelection.selectedRowKeys ?? []).filter(
              (callerKey) => !pageKeys.has(callerKey),
            );
            const pageSelection = internalKeys.map(
              (internalKey) =>
                callerKeyByInternalKey.get(internalKey) ?? internalKey,
            );
            rowSelection.onChange?.(
              [...offPageKeys, ...pageSelection],
              selectedRows,
              info,
            );
          }
        : undefined,
    } as TableRowSelection<T>;
  }, [callerKeyByInternalKey, rowSelection]);

  const footerContent = footer ? footer(resolvedData as readonly T[]) : null;

  const pageChanger = (
    newPagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<T> | SorterResult<T>[],
    extra: TableCurrentDataSource<T>,
  ) => {
    if (onTableChange) {
      onTableChange(newPagination, filters, sorter, extra);
    }
    if (extra.action === "paginate") {
      setPagination({
        ...pagination,
        current: newPagination.current,
        pageSize: newPagination.pageSize,
      });
    } else if (extra.action === "filter" || extra.action === "sort") {
      setPagination({ ...pagination, current: 1 });
    }
  };

  return (
    <div ref={containerRef}>
      <Table
        className={["pagination-table", className ?? "shadow-md"]
          .filter(Boolean)
          .join(" ")}
        size="small"
        bordered={bordered}
        columns={columns}
        ref={tableRef}
        dataSource={resolvedData}
        virtual={true}
        rowKey="__paginatedTableRowKey"
        rowSelection={internalRowSelection}
        key={tableHeight}
        onChange={pageChanger}
        onRow={(record) => {
          const base = onRow ? onRow(record) : {};
          if (!onRowContextMenu) return base;
          return {
            ...base,
            onContextMenu: (e: React.MouseEvent<HTMLElement>) => {
              e.preventDefault();
              onRowContextMenu(record as T, e);
            },
          };
        }}
        scroll={{ y: tableHeight }}
        pagination={
          footerContent
            ? { ...pagination, showTotal: () => footerContent }
            : pagination
        }
      />
    </div>
  );
}
